import { Controller } from "@hotwired/stimulus"
import { showConfirmModal } from "../lib/custom_confirm"

// Save-confirmation for every AppSetting edit/create, to prevent accidental
// saves. Money settings (entered in dollars) get a value-aware message so a
// fat-finger (10000 vs 1000) is caught at the dialog; percentage settings
// restate the value as "to 40%" so a wrong-scale entry is caught; all other settings get a generic confirmation whose
// verb matches the action (create vs update).
//
//   dollars   10000   -> "…to $10,000.00?"
//   percent   40      -> "…to 40%?"
//   generic   (edit)  -> "Are you sure you want to update this App Setting?"
//   generic   (new)   -> "Are you sure you want to create this App Setting?"
//
// The confirmation uses the app's styled modal (showConfirmModal) instead of the
// browser-native window.confirm. Because that modal is async (Promise-based) and
// form submit is synchronous, we always prevent the original submit, then
// re-submit once the user accepts — guarded by `#confirmed` to avoid re-prompting.
//
// Wired onto the edit/new <form> by Admin::AppSettingsHelper#setting_confirm_data.
export default class extends Controller {
  static values = { kind: String, label: String, editing: Boolean }

  static UPDATE_MESSAGE = "Are you sure you want to update this App Setting?"
  static CREATE_MESSAGE = "Are you sure you want to create this App Setting?"

  #confirmed = false

  confirm (event) {
    // Re-entrant submit fired by requestSubmit() after the user accepted the
    // modal — let it through to Turbo without re-prompting.
    if (this.#confirmed) {
      this.#confirmed = false
      return
    }

    // A Slack-channel "Create & save" click carries its own creation
    // confirmation (slack-channel-create) and sets create_channel=1; don't
    // double-prompt — let the native submit through.
    if (this.element.querySelector('input[name="create_channel"]')?.value === "1") return

    event.preventDefault()

    // Force the neutral variant + an action-matched button label. Auto-detection
    // keys off the message text, and a setting label like "Account Lock …" would
    // otherwise trip the danger ("Lock") pattern and mis-style the dialog.
    showConfirmModal(this.message(), {
      variant: "primary",
      title: this.editingValue ? "Update App Setting" : "Create App Setting",
      confirmText: this.editingValue ? "Update" : "Create",
      // Red "Update" to flag a sensitive value change; create keeps the neutral
      // default. confirmClass recolors only the button, not the whole dialog.
      confirmClass: this.editingValue ? "gl-btn gl-btn-danger" : undefined
    }).then((confirmed) => {
      // Bail if the user cancelled, or if the form was removed / a concurrent
      // (double-click) confirm already re-submitted while this modal was open.
      if (!confirmed || !this.element.isConnected) return

      this.#confirmed = true
      this.element.requestSubmit()
    })
  }

  message () {
    switch (this.kindValue) {
      case "dollars":
        return this.dollarsMessage()
      case "percent":
        return this.percentMessage()
      default:
        return this.genericMessage()
    }
  }

  dollarsMessage () {
    const human = this.formatDollars(this.rawValue())
    // Fall back to the generic prompt if the value can't be interpreted — the
    // save is still confirmed; server-side validation reports a bad value.
    if (human === null) return this.genericMessage()

    return this.restate(human)
  }

  percentMessage () {
    const raw = this.rawValue()
    // Empty / non-numeric input → generic prompt rather than "...to abc%?"
    // (server validation rejects it either way; this is the dialog UX).
    if (!raw || Number.isNaN(parseFloat(raw))) return this.genericMessage()

    return this.restate(`${raw}%`)
  }

  // "Are you sure you want to {update|create} <label> to <human>?" — verb tracks
  // the create/edit path like genericMessage does.
  restate (human) {
    const verb = this.editingValue ? "update" : "create"
    return `Are you sure you want to ${verb} ${this.labelValue} to ${human}?`
  }

  genericMessage () {
    return this.editingValue
      ? this.constructor.UPDATE_MESSAGE
      : this.constructor.CREATE_MESSAGE
  }

  rawValue () {
    const input = this.element.querySelector('[name="app_setting[value]"]')
    return input ? input.value : ""
  }

  formatDollars (raw) {
    const n = parseFloat(raw)
    if (Number.isNaN(n)) return null

    // The input already holds dollars (the form posts dollars for _cents
    // settings and the controller converts to cents on save). Do NOT divide.
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}
